import { useEffect, useState, type KeyboardEvent } from 'react'
import { api, ApiError } from '../lib/api'
import type { ProjectSummary } from '../lib/project'

interface AdminUser {
  id: string
  email: string | null
  displayName: string
  role: 'user' | 'admin'
  suspendedAt: string | null
  suspensionReason: string | null
  createdAt: string
  lastLoginAt: string
}
interface AdminReport { id: string; youtubeVideoId: string; videoTitle: string; channelTitle: string | null; reason: string; details: string; status: 'pending' | 'reviewing' | 'resolved' | 'dismissed'; reporterReply: string; createdAt: string; updatedAt: string; reporter: string; videoSuspended: boolean; projects: Array<{ id: string; title: string; creatorName: string; visibility: string; moderationStatus: string; videoSuspended: boolean }> }

export function AdminPage() {
  const [tab, setTab] = useState<'projects' | 'users' | 'reports'>('projects')
  const [query, setQuery] = useState('')
  const [projects, setProjects] = useState<ProjectSummary[]>([])
  const [users, setUsers] = useState<AdminUser[]>([])
  const [reports, setReports] = useState<AdminReport[]>([])
  const [reportStatus, setReportStatus] = useState('')
  const [reportDrafts, setReportDrafts] = useState<Record<string, { status: string; reply: string; reason: string }>>({})
  const [reportSaving, setReportSaving] = useState<Record<string, boolean>>({})
  const [reportFeedback, setReportFeedback] = useState<Record<string, { kind: 'success' | 'error'; text: string }>>({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const adminTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const tabs: Array<typeof tab> = ['projects', 'users', 'reports']; const index = tabs.indexOf(tab)
    const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : -1
    if (next < 0) return; event.preventDefault(); const value = tabs[next]!; setTab(value); setQuery(''); queueMicrotask(() => document.querySelector<HTMLButtonElement>(`[data-admin-tab="${value}"]`)?.focus())
  }

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      if (tab === 'projects') {
        setProjects(await api<ProjectSummary[]>(`/api/admin/projects?q=${encodeURIComponent(query)}`))
      } else if (tab === 'users') {
        setUsers(await api<AdminUser[]>(`/api/admin/users?q=${encodeURIComponent(query)}`))
      } else {
        const rows = await api<AdminReport[]>(`/api/admin/reports?q=${encodeURIComponent(query)}&status=${encodeURIComponent(reportStatus)}`)
        setReports(rows)
        setReportDrafts(Object.fromEntries(rows.map((row) => [row.id, { status: row.status, reply: row.reporterReply, reason: '' }])))
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : '無法讀取管理資料')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // `load` deliberately follows tab only; search is submitted manually.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab])

  const moderation = async (project: ProjectSummary) => {
    const status = project.moderationStatus === 'hidden' ? 'active' : 'hidden'
    const label = status === 'hidden' ? '隱藏' : '恢復'
    const reason = window.prompt(`請輸入${label}「${project.title}」的原因（必填）：`)
    if (!reason?.trim()) return
    try {
      await api(`/api/admin/projects/${project.id}/moderation`, {
        method: 'POST',
        body: JSON.stringify({ status, reason }),
      })
      setMessage(`作品已${label}，處置紀錄已保存。`)
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : `無法${label}作品`)
    }
  }

  const suspension = async (user: AdminUser) => {
    const suspended = !user.suspendedAt
    const label = suspended ? '停權' : '解除停權'
    const reason = window.prompt(`請輸入${label}「${user.displayName}」的原因（必填）：`)
    if (!reason?.trim()) return
    try {
      await api(`/api/admin/users/${user.id}/suspension`, {
        method: 'POST',
        body: JSON.stringify({ suspended, reason }),
      })
      setMessage(`使用者已${label}，處置紀錄已保存。`)
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : `無法${label}使用者`)
    }
  }
  const updateReport = async (report: AdminReport) => { const draft = reportDrafts[report.id]; setReportSaving((value) => ({ ...value, [report.id]: true })); setReportFeedback((value) => { const next = { ...value }; delete next[report.id]; return next }); try { const saved = await api<{ id: string; status: AdminReport['status']; reporterReply: string; updatedAt: string }>(`/api/admin/reports/${report.id}`, { method: 'POST', body: JSON.stringify({ status: draft.status, reporterReply: draft.reply }) }); setReports((value) => value.map((current) => current.id === report.id ? { ...current, status: saved.status, reporterReply: saved.reporterReply, updatedAt: saved.updatedAt } : current)); setReportDrafts((value) => ({ ...value, [report.id]: { ...value[report.id]!, status: saved.status, reply: saved.reporterReply } })); setReportFeedback((value) => ({ ...value, [report.id]: { kind: 'success', text: '已儲存。' } })) } catch (caught) { setReportFeedback((value) => ({ ...value, [report.id]: { kind: 'error', text: caught instanceof ApiError ? caught.message : '無法儲存案件更新，請再試一次。' } })) } finally { setReportSaving((value) => ({ ...value, [report.id]: false })) } }
  const suspendVideo = async (report: AdminReport, suspended: boolean) => { const draft = reportDrafts[report.id]; if (!draft.reason.trim()) { setError('請填寫 1-500 字處置原因'); return } const count = report.projects.length; if (!window.confirm(suspended ? `這會讓使用這支影片的所有 ${count} 個作品停止播放、無法編輯，也無法公開。個別作品原本的公開或隱藏狀態不會改變。` : '恢復後，各作品會回到停用前的公開、私人或隱藏狀態。')) return; try { await api(`/api/admin/videos/${report.youtubeVideoId}/suspension`, { method: 'POST', body: JSON.stringify({ suspended, reason: draft.reason }) }); setMessage(suspended ? `影片已停用，${count} 個作品目前無法使用。` : '影片已恢復。'); await load() } catch (caught) { setError(caught instanceof ApiError ? caught.message : '無法更新影片狀態') } }

  return (
    <div className="page admin-page">
      <header className="page-header">
        <span className="eyebrow">MVP 管理</span>
        <h1>內容與帳號處置</h1>
        <p>只做搜尋、公開作品隱藏／恢復、使用者停權／解除停權；每次處置都必須填寫原因。</p>
      </header>
      <div className="admin-tabs" role="tablist" aria-label="管理資料類型">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'projects'}
          data-admin-tab="projects"
          className={tab === 'projects' ? 'admin-tab admin-tab--active' : 'admin-tab'}
          onKeyDown={adminTabKeyDown} onClick={() => {
            setTab('projects')
            setQuery('')
          }}
        >
          作品
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'users'}
          data-admin-tab="users"
          className={tab === 'users' ? 'admin-tab admin-tab--active' : 'admin-tab'}
          onKeyDown={adminTabKeyDown} onClick={() => {
            setTab('users')
            setQuery('')
          }}
        >
          使用者
        </button>
        <button data-admin-tab="reports" type="button" role="tab" aria-selected={tab === 'reports'} className={tab === 'reports' ? 'admin-tab admin-tab--active' : 'admin-tab'} onKeyDown={adminTabKeyDown} onClick={() => { setTab('reports'); setQuery('') }}>影片檢舉</button>
      </div>
      <form
        className="toolbar"
        onSubmit={(event) => {
          event.preventDefault()
          void load()
        }}
      >
        <label className="search-field search-field--inline">
          <span className="sr-only">搜尋</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={tab === 'projects' ? '作品名稱、ID 或 YouTube ID' : tab === 'users' ? '名稱、Email 或使用者 ID' : '影片名稱、YouTube ID、作品 ID 或檢舉 ID'}
          />
        </label>
        <button className="button button--primary" type="submit">
          搜尋
        </button>
        {tab === 'reports' && <label><span className="sr-only">狀態</span><select value={reportStatus} onChange={(event) => setReportStatus(event.target.value)}><option value="">全部狀態</option><option value="pending">待處理</option><option value="reviewing">處理中</option><option value="resolved">已處理</option><option value="dismissed">不成立</option></select></label>}
      </form>
      {message && <div className="alert alert--success">{message}</div>}
      {error && <div className="alert alert--danger">{error}</div>}
      {loading ? (
        <p className="page-status">正在讀取…</p>
      ) : tab === 'projects' ? (
        <div className="admin-list">
          {projects.map((project) => (
            <article className="admin-row" key={project.id}>
              <div>
                <div className="project-card__meta">
                  <span className={`status-pill status-pill--${project.visibility}`}>
                    {project.visibility === 'public' ? '公開' : '私人'}
                  </span>
                  <span className={`status-pill status-pill--${project.moderationStatus}`}>
                    {project.moderationStatus === 'hidden' ? '已隱藏' : '正常'}
                  </span>
                </div>
                <h2>{project.title}</h2>
                <p>
                  {project.creatorName ?? '未知使用者'} · {project.youtubeVideoId} · {project.id}
                </p>
              </div>
              <button className={project.moderationStatus === 'hidden' ? 'button' : 'button button--danger'} type="button" onClick={() => void moderation(project)}>
                {project.moderationStatus === 'hidden' ? '恢復作品' : '隱藏作品'}
              </button>
            </article>
          ))}
          {!projects.length && <div className="empty-state">沒有符合的作品。</div>}
        </div>
      ) : tab === 'users' ? (
        <div className="admin-list">
          {users.map((user) => (
            <article className="admin-row" key={user.id}>
              <div>
                <div className="project-card__meta">
                  <span className="status-pill">{user.role === 'admin' ? '管理者' : '使用者'}</span>
                  {user.suspendedAt && <span className="status-pill status-pill--hidden">已停權</span>}
                </div>
                <h2>{user.displayName}</h2>
                <p>
                  {user.email ?? '無 Email'} · {user.id}
                </p>
                {user.suspensionReason && <small>目前原因：{user.suspensionReason}</small>}
              </div>
              <button
                className={user.suspendedAt ? 'button' : 'button button--danger'}
                type="button"
                disabled={user.role === 'admin'}
                onClick={() => void suspension(user)}
              >
                {user.suspendedAt ? '解除停權' : '停權'}
              </button>
            </article>
          ))}
          {!users.length && <div className="empty-state">沒有符合的使用者。</div>}
        </div>
      ) : <div className="admin-list">{reports.map((report) => { const draft = reportDrafts[report.id] ?? { status: report.status, reply: '', reason: '' }; const saving = Boolean(reportSaving[report.id]); const feedback = reportFeedback[report.id]; return <article className="admin-row admin-report" key={report.id}><div><section className="admin-report__video"><img src={`https://i.ytimg.com/vi/${report.youtubeVideoId}/mqdefault.jpg`} alt={`${report.videoTitle} 的 YouTube 縮圖`} referrerPolicy="no-referrer" /><div><span className="eyebrow">被檢舉影片</span><h2>{report.videoTitle}</h2>{report.channelTitle && <p>{report.channelTitle}</p>}<p>YouTube ID：{report.youtubeVideoId}</p><p>案件 ID：{report.id}</p></div></section><p>檢舉原因：{report.reason}</p><p>補充說明：{report.details || '未提供補充說明'}</p><p>檢舉者：{report.reporter}</p><p>送出於 {new Date(report.createdAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}</p><details><summary>同影片作品（{report.projects.length}）</summary>{report.projects.map((project) => <p key={project.id}>{project.title} · {project.id} · {project.creatorName} · {project.visibility} · {project.moderationStatus === 'hidden' ? '已隱藏' : '正常'} · {project.videoSuspended ? '影片已停用' : '正常'}</p>)}</details></div><div className="admin-report__controls"><label>案件處理<select disabled={saving} value={draft.status} onChange={(event) => setReportDrafts((value) => ({ ...value, [report.id]: { ...draft, status: event.target.value } }))}><option value="pending">待處理</option><option value="reviewing">處理中</option><option value="resolved">已處理</option><option value="dismissed">不成立</option></select></label><label>回覆檢舉者（選填）<textarea disabled={saving} maxLength={1000} placeholder="這段回覆會顯示給檢舉者。" value={draft.reply} onChange={(event) => setReportDrafts((value) => ({ ...value, [report.id]: { ...draft, reply: event.target.value } }))} /></label><button className="button button--primary" type="button" disabled={saving} onClick={() => void updateReport(report)}>{saving ? '儲存中…' : '儲存更新'}</button>{feedback && <p role={feedback.kind === 'error' ? 'alert' : 'status'}>{feedback.text}</p>}<p>影片狀態：{report.videoSuspended ? '已停用' : '正常'}</p><label>處置原因<input value={draft.reason} onChange={(event) => setReportDrafts((value) => ({ ...value, [report.id]: { ...draft, reason: event.target.value } }))} /></label><button className={report.videoSuspended ? 'button' : 'button button--danger'} type="button" onClick={() => void suspendVideo(report, !report.videoSuspended)}>{report.videoSuspended ? '恢復這支影片' : '停用這支影片'}</button></div></article> })}{!reports.length && <div className="empty-state">沒有符合的影片檢舉。</div>}</div>}
    </div>
  )
}
